import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

/**
 * ModalDialogWrapper is a reusable modal dialog component that wraps its children
 * with Material-UI's Dialog, providing customizable width, height, and styling options.
 * It displays a title with an optional icon, a close button, and renders its children inside the dialog.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} [props.open] - Controls whether the dialog is open.
 * @param {Function} [props.close] - Callback function to close the dialog.
 * @param {React.ElementType} props.Icon - Icon component to display in the dialog title.
 * @param {React.ReactNode} props.title - Title content of the dialog.
 * @param {Object} [props.options] - Additional style options for the dialog paper (e.g., width, minHeight).
 * @param {React.ReactNode} props.children - Content to render inside the dialog.
 *
 * @example
 * <ModalDialogWrapper
 *   open={isOpen}
 *   close={handleClose}
 *   Icon={InfoIcon}
 *   title="Dialog Title"
 *   options={{ width: '60%' }}
 * >
 *   <div>Dialog Content</div>
 * </ModalDialogWrapper>
 */
function ModalDialogWrapper({
  open = () => undefined,
  close = () => undefined,
  Icon,
  title,
  options = null,
  children,
}) {
  const theme = useTheme();

  const defaultOptions = {
    width: '80%',
    minHeight: 100,
    minWidth: '80%',
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
  };

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          ...finalOptions,
        },
      }}
      maxWidth="xs"
      open={open}>
      <Box>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            py: 1,
            gap: 0.5,
            borderBottom: `1px solid${theme.palette.grey[300]}`,
            mb: 2,
          }}>
          <Icon color="primary" />
          <Typography color="primary" variant="body">
            {title}
          </Typography>
          <IconButton
            onClick={close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            }}>
            <Close />
          </IconButton>
        </DialogTitle>
      </Box>
      <DialogContent sx={{ p: 1 }}>{children}</DialogContent>
    </Dialog>
  );
}

export default ModalDialogWrapper;
